import { Button, HStack } from '@chakra-ui/react';
import type { CompanionDismissReason, CompanionSnoozeOfferDto } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

/**
 * The three answers a brand can give a card, plus the way back.
 *
 * "Later" appears only when the server says it would grant a delay, and shows
 * the delay it would actually grant. A card too close to its own deadline gets
 * no such option rather than a promise the server would then refuse.
 */

interface CompanionDismissChipsProps {
  snoozeOffer: CompanionSnoozeOfferDto | null;
  onChoose: (reason: CompanionDismissReason) => void;
  onCancel: () => void;
  isPending: boolean;
}

const chipStyles = {
  variant: 'outline',
  h: '32px',
  px: '12px',
  borderRadius: '8px',
  borderColor: 'border.emphasized',
  fontFamily: 'heading',
  fontSize: '12.5px',
  fontWeight: '600',
  color: 'ink',
  _hover: { bg: 'softCream' },
} as const;

export const CompanionDismissChips = ({
  snoozeOffer,
  onChoose,
  onCancel,
  isPending,
}: CompanionDismissChipsProps) => {
  const { t } = useTranslation();

  return (
    <HStack gap="8px" wrap="wrap" data-testid="companion-dismiss-chips">
      <Button {...chipStyles} onClick={() => onChoose('irrelevant')} disabled={isPending}>
        {t('companion.panel.dismiss.irrelevant')}
      </Button>
      <Button {...chipStyles} onClick={() => onChoose('done')} disabled={isPending}>
        {t('companion.panel.dismiss.done')}
      </Button>
      {snoozeOffer ? (
        <Button {...chipStyles} onClick={() => onChoose('later')} disabled={isPending}>
          {t('companion.panel.dismiss.later', { count: snoozeOffer.days })}
        </Button>
      ) : null}
      <Button
        variant="plain"
        h="32px"
        px="0"
        ml="auto"
        fontFamily="heading"
        fontSize="12.5px"
        fontWeight="600"
        color="inkSubtle"
        _hover={{ color: 'inkMuted' }}
        onClick={onCancel}
        disabled={isPending}
      >
        {t('companion.panel.dismiss.cancel')}
      </Button>
    </HStack>
  );
};
